import * as React from "react";
import { cn } from "@/lib/utils";

/* ── Sheet Context ───────────────────────────────────────────────────── */

interface SheetContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const SheetContext = React.createContext<SheetContextValue | null>(null);

function useSheetContext() {
  const ctx = React.useContext(SheetContext);
  if (!ctx) throw new Error("Sheet components must be used within <Sheet>");
  return ctx;
}

/* ── Sheet Root ──────────────────────────────────────────────────────── */

export interface SheetProps {
  children: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/** Sheet root - slide-out panel manager. */
function Sheet({ children, open: controlledOpen, defaultOpen = false, onOpenChange }: SheetProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const setOpen = React.useCallback(
    (value: boolean) => {
      if (controlledOpen === undefined) setInternalOpen(value);
      onOpenChange?.(value);
    },
    [controlledOpen, onOpenChange]
  );

  return (
    <SheetContext.Provider value={{ open, setOpen }}>
      {children}
    </SheetContext.Provider>
  );
}

/* ── SheetTrigger ────────────────────────────────────────────────────── */

const SheetTrigger = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, onClick, ...props }, ref) => {
    const { setOpen } = useSheetContext();

    return (
      <button
        ref={ref}
        type="button"
        onClick={(e) => {
          setOpen(true);
          onClick?.(e);
        }}
        className={cn(className)}
        {...props}
      />
    );
  }
);
SheetTrigger.displayName = "SheetTrigger";

/* ── SheetClose ──────────────────────────────────────────────────────── */

const SheetClose = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, onClick, ...props }, ref) => {
    const { setOpen } = useSheetContext();

    return (
      <button
        ref={ref}
        type="button"
        onClick={(e) => {
          setOpen(false);
          onClick?.(e);
        }}
        className={cn(className)}
        {...props}
      />
    );
  }
);
SheetClose.displayName = "SheetClose";

/* ── SheetContent ────────────────────────────────────────────────────── */

import { createPortal } from "react-dom";

export interface SheetContentProps extends React.HTMLAttributes<HTMLDivElement> {
  side?: "top" | "bottom" | "left" | "right";
}

const SheetContent = React.forwardRef<HTMLDivElement, SheetContentProps>(
  ({ className, children, side = "right", ...props }, ref) => {
    const { open, setOpen } = useSheetContext();

    // Close on Escape
    React.useEffect(() => {
      if (!open) return;
      const handler = (e: KeyboardEvent) => {
        if (e.key === "Escape") setOpen(false);
      };
      document.addEventListener("keydown", handler);
      return () => document.removeEventListener("keydown", handler);
    }, [open, setOpen]);

    // Prevent body scroll
    React.useEffect(() => {
      if (open) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
      return () => {
        document.body.style.overflow = "";
      };
    }, [open]);

    if (!open) return null;

    // Panels float: a gap from the screen edge, rounded corners and a soft
    // shadow. The frame positions the panel; the panel takes the caller's
    // className (width, padding, layout), so w-full means "fill the frame".
    const frameClasses = {
      top: "inset-x-0 top-0 justify-center",
      bottom: "inset-x-0 bottom-0 items-end justify-center",
      left: "inset-y-0 left-0 w-full justify-start",
      right: "inset-y-0 right-0 w-full justify-end",
    };
    const panelClasses = {
      top: "w-full max-h-[calc(100dvh-1rem)] animate-slide-in-top",
      bottom: "w-full max-h-[calc(100dvh-1rem)] animate-slide-in-bottom",
      left: "h-full w-3/4 max-w-sm animate-slide-in-left",
      right: "h-full w-3/4 max-w-sm animate-slide-in-right",
    };

    return createPortal(
      <div className="fixed inset-0 z-50">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-[#002a31]/30 backdrop-blur-[2px] animate-fade-in"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
        {/* Frame (positions the floating panel) */}
        <div className={cn("pointer-events-none fixed z-50 flex p-2 sm:p-3", frameClasses[side])}>
          <div
            ref={ref}
            role="dialog"
            aria-modal="true"
            className={cn(
              "pointer-events-auto relative flex flex-col gap-4 overflow-y-auto p-6",
              "rounded-[24px] bg-card text-card-foreground shadow-[0_24px_64px_-20px_rgba(0,42,49,0.45)]",
              panelClasses[side],
              className
            )}
            {...props}
          >
            {children}
            {/* Close button */}
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute right-4 top-4 inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-[#f8f3eb] text-[#003a43] transition-colors hover:bg-[#f1ece2] dark:bg-muted dark:text-foreground"
              aria-label="Close"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }
);
SheetContent.displayName = "SheetContent";

/* ── SheetHeader ─────────────────────────────────────────────────────── */

const SheetHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex flex-col space-y-2 text-center sm:text-left", className)}
      {...props}
    />
  )
);
SheetHeader.displayName = "SheetHeader";

/* ── SheetFooter ─────────────────────────────────────────────────────── */

const SheetFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
        className
      )}
      {...props}
    />
  )
);
SheetFooter.displayName = "SheetFooter";

/* ── SheetTitle ──────────────────────────────────────────────────────── */

const SheetTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h2
      ref={ref}
      className={cn("text-lg font-semibold text-foreground", className)}
      {...props}
    />
  )
);
SheetTitle.displayName = "SheetTitle";

/* ── SheetDescription ────────────────────────────────────────────────── */

const SheetDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
SheetDescription.displayName = "SheetDescription";

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
